/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */
import React, {useEffect, useState} from 'react';

import {
  APIConstants,
  ExtJS
} from '@sonatype/nexus-ui-plugin';

import {
  ContentBody,
  Page,
  PageHeader,
  PageTitle
 } from '@sonatype/nexus-ui-plugin';

import {
  NxH3,
  NxTile,
  NxInfoAlert,
  NxP
} from '@sonatype/react-shared-components';

import UIStrings from '../../../constants/UIStrings';
import firewallLogoDark from '../../../../../art/logos/sonatype-firewall-logo-dark.png';
import firewallLogoLight from '../../../../../art/logos/sonatype-firewall-logo-light.svg';

import {faExclamationTriangle} from "@fortawesome/free-solid-svg-icons";
import "./MaliciousRisk.scss";
import MaliciousRiskOnDiskComponentCount from './MaliciousRiskOnDiskComponentCount';

const {
  TITLE,
  DESCRIPTION,
  MALWARE_REMEDIATION: {DESCRIPTION: MALWARE_DESCRIPTION, REMEDIATION_STEPS, DOWNLOAD_CSV, CURRENT_TASKS_CONFIGURED},
  OPEN_SOURCE_MALWARE: {TITLE: OPEN_SOURCE_MALWARE_TITLE, INFO},
  POWERED_BY,
  MALWARE_MANAGEMENT_TASKS_COUNT,
} = UIStrings.MALICIOUS_RISK;

const {MALWARE_COMPONENTS_CSV} = APIConstants.REST.PUBLIC;

function useFirewallLogo() {
  const htmlEl = document.documentElement;
  const [isDarkMode, setDarkMode] = useState(htmlEl.classList.contains('nx-html--dark-mode'));

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setDarkMode(htmlEl.classList.contains('nx-html--dark-mode'));
    });

    observer.observe(htmlEl, {attributes: true, attributeFilter: ['class']});

    return () => observer.disconnect();
  }, []);

  return isDarkMode ? firewallLogoDark : firewallLogoLight;
}

export default function MalwareRemediation() {
  const downloadCSVUrl = ExtJS.urlOf(MALWARE_COMPONENTS_CSV);
  const malwareManagementTasksCount = ExtJS.useState(() => ExtJS.state().getValue(MALWARE_MANAGEMENT_TASKS_COUNT)) ?? 0;
  const firewallLogo = useFirewallLogo();

  return (
      <Page className="nxrm-malware-risk">
        <PageHeader>
          <PageTitle icon={faExclamationTriangle} text={TITLE} description={DESCRIPTION}/>
        </PageHeader>
        <ContentBody>
          <NxTile className="nxrm-malware-remediation">
            <NxTile.Content>
              <div className="nxrm-remediation-content">
                <MaliciousRiskOnDiskComponentCount />
                <NxP className="nxrm-remediation-description">{MALWARE_DESCRIPTION}</NxP>
                <div className="nxrm-remediation-steps">
                  <NxH3>{REMEDIATION_STEPS.TITLE}</NxH3>
                  <ol>
                    <li>
                      {REMEDIATION_STEPS.FIRST}
                      <NxP className="nxrm-status-indicator-section">
                        <span role="status"
                              className={`nx-status-indicator nx-status-indicator--${malwareManagementTasksCount === 0 ? 'negative' : 'intermediate'}`}>
                          {CURRENT_TASKS_CONFIGURED(malwareManagementTasksCount.toLocaleString())}</span>
                      </NxP>
                    </li>
                    <li>
                      {REMEDIATION_STEPS.SECOND}
                      <a download className="nx-btn nx-btn--tertiary download-malware-component-csv" href={downloadCSVUrl}>{DOWNLOAD_CSV}</a>
                    </li>
                    <li>{REMEDIATION_STEPS.THIRD}</li>
                    <li>{REMEDIATION_STEPS.FOURTH}</li>
                  </ol>
                </div>
              </div>
              <div>
                <NxInfoAlert>
                  <NxH3>{OPEN_SOURCE_MALWARE_TITLE}</NxH3>
                  {INFO}
                </NxInfoAlert>
              </div>
            </NxTile.Content>
          </NxTile>
          <div className="nxrm-repo-firewall-logo-section">
            <span>{POWERED_BY.TEXT}</span>
            <img
                className="sonatype-repository-firewall-img"
                alt="Sonatype Repository Firewall Logo"
                src={firewallLogo}
            />
          </div>
        </ContentBody>
      </Page>
  )
}
